// Button.jsx - Professional button component for Foundations Risk Aperture
import React from 'react';

const Button = ({ 
  children, 
  variant = 'primary',
  size = 'md',
  icon,
  iconPosition = 'left',
  loading = false,
  disabled = false,
  className = '',
  onClick,
  ...props 
}) => {
  const baseClasses = 'relative overflow-hidden transition-all duration-300 ease-in-out font-medium rounded-lg border focus:outline-none focus:ring-4 focus:ring-opacity-50 disabled:cursor-not-allowed';
  
  const variantClasses = {
    primary: 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 focus:ring-[rgba(0,217,255,0.3)] disabled:bg-blue-300',
    secondary: 'bg-[rgba(255,255,255,0.20)] text-white border-[rgba(255,255,255,0.12)] hover:bg-[#1e3148] focus:ring-[rgba(255,255,255,0.18)] disabled:bg-[rgba(255,255,255,0.08)]',
    success: 'bg-green-600 text-white border-green-600 hover:bg-green-700 focus:ring-[rgba(0,255,136,0.3)] disabled:bg-green-300',
    warning: 'bg-yellow-600 text-white border-yellow-600 hover:bg-yellow-700 focus:ring-[rgba(255,165,0,0.3)] disabled:bg-yellow-300',
    danger: 'bg-red-600 text-white border-red-600 hover:bg-red-700 focus:ring-[rgba(255,68,68,0.3)] disabled:bg-red-300',
    ghost: 'bg-transparent text-[#5a6a8a] hover:bg-[#172a45] border-transparent focus:ring-[rgba(255,255,255,0.18)]',
    outline: 'bg-transparent border-2 border-[rgba(255,255,255,0.18)] text-[#5a6a8a] hover:bg-[rgba(255,255,255,0.03)] focus:ring-[rgba(255,255,255,0.18)]'
  };
  
  const sizeClasses = {
    sm: 'px-3 py-2 text-xs',
    md: 'px-6 py-3 text-sm',
    lg: 'px-8 py-4 text-base',
    xl: 'px-10 py-5 text-lg'
  };
  
  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : '';
  
  const combinedClasses = [
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    disabledClasses,
    className
  ].filter(Boolean).join(' ');

  const handleClick = (e) => {
    if (disabled || loading) return;
    onClick?.(e);
  };

  const LoadingSpinner = () => (
    <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
  );

  const renderContent = () => {
    if (loading) {
      return (
        <>
          <LoadingSpinner />
          <span>Loading...</span>
        </>
      );
    }

    if (icon && iconPosition === 'left') {
      return (
        <>
          <span className="text-lg">{icon}</span>
          <span>{children}</span>
        </>
      );
    }

    if (icon && iconPosition === 'right') {
      return (
        <>
          <span>{children}</span>
          <span className="text-lg">{icon}</span>
        </>
      );
    }

    return children;
  };

  return (
    <button 
      className={combinedClasses}
      onClick={handleClick}
      disabled={disabled || loading}
      {...props}
    >
      {/* Shine effect overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] hover:translate-x-[100%] transition-transform duration-700 ease-out" />
      
      {/* Button content */}
      <div className="relative z-10 flex items-center justify-center gap-2">
        {renderContent()}
      </div>
    </button>
  );
};

// Button variants as separate components for convenience
export const PrimaryButton = (props) => <Button variant="primary" {...props} />;
export const SecondaryButton = (props) => <Button variant="secondary" {...props} />;
export const SuccessButton = (props) => <Button variant="success" {...props} />;
export const WarningButton = (props) => <Button variant="warning" {...props} />;
export const DangerButton = (props) => <Button variant="danger" {...props} />;
export const GhostButton = (props) => <Button variant="ghost" {...props} />;
export const OutlineButton = (props) => <Button variant="outline" {...props} />;

export { Button };